import { useState } from "react";

import { Alert } from "../../components/Alert/Alert";
import { Badge } from "../../components/Badge/Badge";
import { Button } from "../../components/Button/Button";
import { ChipInput } from "../../components/ChipInput/ChipInput";
import { Input } from "../../components/Input/Input";
import { Modal } from "../../components/Modal/Modal";
import { Select, type Option } from "../../components/Select/Select";
// One list, shared with the Source tab's own country filter: a row can be set
// to any country the panel draws a flag for, and the filter has to be able to
// name every one of them back.
import { COUNTRY_OPTIONS } from "../../components/CountryCell/countryOptions";
import type { BotProfile, BotsAccountAgeConfig } from "./config/types";
import { DialogRow } from "./DialogRow";
import {
  blockedLabel,
  columnID,
  formatCount,
  isNumberType,
  numberError,
  MAX_AGE_DAYS,
  MAX_NAME_RUNES,
  NOT_GIVEN,
  type CardKey,
} from "./roster";
import type { RosterInput, RosterRow } from "./types";
import styles from "./SourceTab.module.css";

/**
 * One roster row, edited by hand — the way a typo is fixed without exporting a
 * CSV, correcting one cell and importing it back.
 *
 * The fields are exactly the CSV's, in the CSV's own order: name, avatar,
 * country, behaviour, account age, and one row per declared card key. They are
 * the fields the PATCH replaces — all of them, every time — because «Save row»
 * means what is on the screen, so a request that omitted a field would be the
 * browser keeping a value the operator had just cleared. (This sentence used to
 * carry a count of them. A count is wrong the first time a field is added.)
 *
 * Values are held as TEXT while the dialog is open and turned into JSON on the
 * way out, which is what the CSV reader does with a cell and for the same
 * reason: an empty box is «this key is not given», never a zero and never an
 * empty string. That difference is the whole point of the card being a card,
 * and the account age is held the same way for the same reason — an empty box
 * there is «draw it», and a stored 0 is «registered today», which are two
 * different rows.
 *
 * Mockups: t-103/bots-source.html §3 and t-117/bots-account-age.html §3, both
 * right-hand panel.
 */

/** A draft value: text for the scalar types, a list for `string[]`. */
type DraftValue = string | string[];

/** The shared country list with this dialog's own «empty» at the top: here an
 *  unset country is not «any country», it is «whatever the guild is». */
const ROW_COUNTRY_OPTIONS: Option[] = [
  {
    value: "",
    label: "— any — follow the guild",
    muted: true,
    separatorAfter: true,
  },
  ...COUNTRY_OPTIONS,
];

interface Props {
  /** The row being edited; null when this dialog will create one. */
  row: RosterRow | null;
  /** What the boxes open with — the row's own values, or a copy of them. */
  initial: RosterInput;
  title: string;
  cardKeys: CardKey[];
  /**
   * `profile.allowed_avatars` — the project's whitelist, and a real check
   * rather than a hint: it is applied when the bot is CREATED. A non-empty one
   * turns the field into a picker; an empty one leaves it free text, because a
   * project that declared no list has not said anything to hold a value to.
   */
  allowedAvatars: string[];
  /** `bots.profiles` — the behaviours this project declares. */
  profiles: BotProfile[];
  /**
   * `bots.account_age` — the range an empty age box falls back to.
   *
   * Passed in rather than described in the abstract because the helper under
   * that box quotes the two numbers: «draw it from the range» is not an
   * instruction anybody can act on until they know what the range is, and this
   * project's is the only one that matters here.
   */
  accountAge: BotsAccountAgeConfig;
  /**
   * The display names of the OTHER rows this page has loaded, so a clash can
   * be named before the server names it. It is what this page KNOWS, not what
   * is true of the whole table — see the hint under the name box.
   *
   * UNIQUENESS IS THE SERVER'S. Fifty rows out of five thousand cannot answer
   * the question, so a duplicate the page never loaded comes back as a 409
   * `name_taken` and is printed in the alert at the top of this dialog. What
   * this list buys is the round trip on the clash the operator can already see.
   */
  takenNames: string[];
  saving: boolean;
  /** What the last save was refused for, already turned into a sentence. */
  error: string | null;
  onClose: () => void;
  onSave: (input: RosterInput) => void;
}

export function EditRosterRowModal({
  row,
  initial,
  title,
  cardKeys,
  allowedAvatars,
  profiles,
  accountAge,
  takenNames,
  saving,
  error,
  onClose,
  onSave,
}: Props) {
  const [name, setName] = useState(initial.display_name);
  const [avatar, setAvatar] = useState(initial.avatar ?? "");
  const [country, setCountry] = useState(initial.country ?? "");
  const [profileKey, setProfileKey] = useState(initial.profile_key ?? "");
  // Text, like every card value beside it: an empty box has to survive being
  // empty while it is being retyped, and a `number | null` box would have to
  // decide what «» is on every keystroke.
  const [age, setAge] = useState(
    initial.age_days === null ? "" : String(initial.age_days),
  );
  const [values, setValues] = useState<Record<string, DraftValue>>(() =>
    draftOf(initial, cardKeys),
  );

  const statKeys = cardKeys.filter((k) => k.kind === "stat");
  const metaKeys = cardKeys.filter((k) => k.kind === "meta");

  // A whitelist the project actually filled in turns the field into a picker;
  // an empty one leaves it free text — the guild template's Logo field, and the
  // players page's avatar rule before it.
  const useAvatarSelect = allowedAvatars.length > 0;
  // A value the whitelist does not list. It SAVES — nothing on this row is
  // checked against the list — and it is checked when the bot is created, where
  // it comes to nothing. So it is kept, selected and marked, rather than
  // dropped here or refused: dropping it would be this dialog editing a value
  // nobody touched, and refusing it would be it inventing a rule the row does
  // not have.
  const avatarOrphan =
    useAvatarSelect && avatar !== "" && !allowedAvatars.includes(avatar);

  const trimmed = name.trim();
  // EXACTLY, because `bot_roster_name_uniq` is `UNIQUE (game_id,
  // display_name)` with no `lower()` in it and every comparison behind it —
  // TakenNames, NameTaken, MarkBlockedRosterRows — is byte for byte. `mashak`
  // beside `MashaK` is two lawful rows, and a dialog that refused the second
  // stated something untrue about the table and left no way round it.
  const nameTaken = takenNames.some((n) => n === trimmed);
  // A clash of case alone is worth SAYING and not worth refusing: it will
  // save, and two rows a human reads as one name is the sort of thing an
  // operator wants to be told about while they can still change their mind.
  const caseClash =
    !nameTaken &&
    trimmed !== "" &&
    takenNames.some((n) => n.toLowerCase() === trimmed.toLowerCase());
  // A row the sweep blocked is blocked over the name it still carries. Once
  // that name is changed the reason is a statement about a value that is no
  // longer there, so the hint goes with it.
  const blockedOnThisName =
    row?.blocked_reason === "name_taken" &&
    trimmed === initial.display_name.trim();

  const tooLong = [...trimmed].length > MAX_NAME_RUNES;
  const badNumbers = cardKeys.filter((k) => numberError(k, values[columnID(k)]));
  const nameError = tooLong
    ? `A name is at most ${MAX_NAME_RUNES} characters.`
    : nameTaken
      ? "Another row of this table already carries this name."
      : null;
  const ageError = accountAgeError(age);
  const blocked =
    trimmed === "" || !!nameError || !!ageError || badNumbers.length > 0;

  function setValue(k: CardKey, next: DraftValue) {
    setValues((prev) => ({ ...prev, [columnID(k)]: next }));
  }

  function save() {
    if (blocked) return;
    onSave({
      display_name: trimmed,
      // Null and not "": an empty box is «this row names no avatar», and the
      // bot is created without one. Nothing is substituted for it.
      avatar: avatar.trim() === "" ? null : avatar.trim(),
      country: country || null,
      profile_key: profileKey || null,
      // Null and not 0. An empty box is «this row does not say», which is what
      // sends the row to `bots.account_age`; a zero is a row that says «this
      // account was registered today», and it is a thing an operator may mean.
      age_days: age.trim() === "" ? null : Number(age.trim()),
      stats: bagOf(statKeys, values),
      meta: bagOf(metaKeys, values),
    });
  }

  return (
    <Modal
      open
      width="lg"
      title={title}
      onClose={onClose}
      closeDisabled={saving}
      footer={
        <>
          {/* The table is written the moment this closes — there is no save
              bar behind it, and a dialog that did not say so would leave the
              operator looking for one. */}
          <span className={styles.footNote}>Saved immediately.</span>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={blocked} loading={saving}>
            {row ? "Save row" : "Add row"}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger" title="This row was refused">
          {error}
        </Alert>
      )}

      <div className={styles.modalStatus}>
        {row ? (
          <>
            <Badge
              variant={
                row.status === "used"
                  ? "success"
                  : row.status === "blocked"
                    ? "danger"
                    : "neutral"
              }
              dot
            >
              {row.status === "blocked"
                ? blockedLabel(row.blocked_reason)
                : row.status}
            </Badge>
            <span>
              {row.status === "used"
                ? "already a bot — editing the row does not change the player it became"
                : row.status === "blocked"
                  ? "a living player carries this name, so the worker skips this row"
                  : "not a bot yet"}
            </span>
          </>
        ) : (
          <>
            <Badge variant="neutral" dot>
              unused
            </Badge>
            <span>a new row, spent after the ones already in the table</span>
          </>
        )}
      </div>

      <DialogRow label="Name">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Name"
          autoFocus
          error={nameError ?? undefined}
        />
        {!nameError && (
          <div
            className={
              blockedOnThisName
                ? `${styles.helper} ${styles.helperBad}`
                : styles.helper
            }
          >
            {blockedOnThisName
              ? "Taken by a living player — rename the row to unblock it."
              : caseClash
                ? "Another row on this page carries this name in a different case. Names are case-sensitive, so both are allowed — this one will save."
                : "Free — no other row on this page carries it. The server checks the whole table and the project's players when the row is saved."}
          </div>
        )}
      </DialogRow>

      {/* Avatar is a field of the ROW, like the name above it and the country
          below, and not a key of the card — so it sits here, in the place the
          table and the CSV also put it, and carries no mono sub-label: `api` in
          DialogRow names a config path the boxes below write INTO, and this one
          writes into the row's own column. Same reasoning as the guild
          template's Logo field. */}
      <DialogRow label="Avatar">
        <div className={styles.avatarField}>
          {useAvatarSelect ? (
            <Select
              aria-label="Avatar"
              value={avatar}
              onChange={setAvatar}
              options={avatarOptions(allowedAvatars, avatar)}
              monospaceValue
              className={`${styles.avatarSelect} ${avatarOrphan ? styles.selectWarn : ""}`}
            />
          ) : (
            <Input
              aria-label="Avatar"
              value={avatar}
              placeholder="e.g. warrior_bronze"
              className={styles.monoInput}
              onChange={(e) => setAvatar(e.target.value)}
            />
          )}
          {!useAvatarSelect ? (
            <div className={styles.helper}>
              Whitelist <code>allowed_avatars</code> is empty → free-text input,
              any string accepted. Leave it empty and the bot is created without
              an avatar.
            </div>
          ) : avatarOrphan ? (
            // The one sentence that is NOT the guild template's. There a value
            // outside the whitelist is legal and is kept; here the whitelist is
            // enforced where it counts, so this says what the row will actually
            // produce — and that it costs the row either way, which is the part
            // an operator cannot guess from a colour.
            <div className={styles.warnHelper}>
              Outside the project&apos;s allowed-avatars whitelist. The whitelist
              is enforced, so a bot created from this row is created{" "}
              <b>without an avatar</b> — the row itself is still spent.
            </div>
          ) : (
            <div className={styles.helper}>
              The in-game asset this bot wears. From the project&apos;s
              allowed-avatars whitelist.
            </div>
          )}
        </div>
      </DialogRow>

      <DialogRow label="Country">
        <Select
          aria-label="Country"
          value={country}
          onChange={setCountry}
          options={ROW_COUNTRY_OPTIONS}
        />
        {/* This list is deliberately NARROWER than the CSV column. What the
            import checks is the shape — two letters, the same check
            players.country is held to — so a file may carry a code no flag is
            drawn for; the form offers the ones this panel can draw. */}
        <div className={styles.helper}>
          Empty follows the guild, the way a generated bot does. It decides
          which language the bot speaks and which clock it lives by. A CSV may
          carry any two-letter code; this list is the ones the panel has a flag
          for.
        </div>
      </DialogRow>

      <DialogRow label="Behaviour">
        <Select
          aria-label="Behaviour"
          value={profileKey}
          onChange={setProfileKey}
          options={[
            { value: "", label: NOT_GIVEN, muted: true },
            ...profiles.map((p) => ({
              value: p.key,
              label: p.key,
              mono: true,
            })),
          ]}
        />
        <div className={styles.helper}>
          Leave empty to draw it by share, the way a generated bot does.
        </div>
      </DialogRow>

      {/* After Behaviour and before the card, which is where it sits in the
          table and in the CSV. The three orders are one order because an
          operator who exports a file, edits a cell and imports it back is
          reading one row in three places. */}
      <DialogRow label="Account age" api="age_days">
        {/* One box and a unit, which the mockup draws with `.unitWrap` and a
            box allowed 130px — not with the range's `.pair`, whose 84px halves
            and 6px gap are the shape of two numbers with a dash between them.
            Here the gap is all that separates the number from the word. */}
        <span className={styles.unitWrap}>
          <span className={styles.unitInput}>
            <Input
              aria-label="Account age"
              value={age}
              placeholder="not given"
              inputMode="numeric"
              onChange={(e) => setAge(e.target.value)}
              error={ageError ?? undefined}
            />
          </span>
          <span className={styles.unit}>days old</span>
        </span>
        {!ageError && (
          <div className={styles.helper}>
            Leave empty to draw it from <code>bots.account_age</code> (
            {formatCount(accountAge.min_days)}–
            {formatCount(accountAge.max_days)} days), the way a generated bot
            does.
          </div>
        )}
      </DialogRow>

      {statKeys.length > 0 && (
        <DialogRow label="Stats" api="profile.stats_schema">
          {statKeys.map((k) => (
            <ValueRow
              key={columnID(k)}
              field={k}
              value={values[columnID(k)]}
              onChange={(next) => setValue(k, next)}
            />
          ))}
        </DialogRow>
      )}

      {metaKeys.length > 0 && (
        <DialogRow label="Meta" api="profile.meta_schema">
          {metaKeys.map((k) => (
            <ValueRow
              key={columnID(k)}
              field={k}
              value={values[columnID(k)]}
              onChange={(next) => setValue(k, next)}
            />
          ))}
        </DialogRow>
      )}

      {cardKeys.length === 0 && (
        <Alert variant="info" title="This project declares no card">
          <code>profile.stats_schema</code> and <code>profile.meta_schema</code>{" "}
          are both empty, so there are no keys for a row to carry. Declare them
          on the project's Config page and the boxes appear here — the rows are
          that schema and nothing else.
        </Alert>
      )}
    </Modal>
  );
}

/**
 * What the avatar picker offers: «not given» first, then the project's
 * whitelist, then — only when the row carries one — the value that is in
 * neither.
 *
 * The first entry is what this dialog needs and a settings form does not: every
 * field goes with every save here, so «no avatar» has to be something an
 * operator can CHOOSE, or a row that arrived with one could never be stripped
 * of it from this screen. `NOT_GIVEN` is the word the card boxes below already
 * use for exactly that.
 *
 * The last is the orphan, kept selected. See `avatarOrphan`: it is legal on the
 * row, it is refused when the bot is made, and a picker that could not show it
 * would silently drop it the moment somebody opened the row to fix a typo.
 */
function avatarOptions(allowed: string[], current: string): Option[] {
  const orphan = current !== "" && !allowed.includes(current);
  return [
    { value: "", label: NOT_GIVEN, muted: true, separatorAfter: true },
    ...allowed.map((v, i) => ({
      value: v,
      label: v,
      mono: true,
      separatorAfter: orphan && i === allowed.length - 1,
    })),
    ...(orphan
      ? [{ value: current, label: current, mono: true, meta: "not in whitelist" }]
      : []),
  ];
}

/** One card key: the box its declared type asks for, and «— not given —»
 *  wherever the key may simply be absent. */
function ValueRow({
  field,
  value,
  onChange,
}: {
  field: CardKey;
  value: DraftValue | undefined;
  onChange: (next: DraftValue) => void;
}) {
  const text = typeof value === "string" ? value : "";
  const list = Array.isArray(value) ? value : [];
  const err = numberError(field, value);
  return (
    <div className={styles.kvRow}>
      <span className={styles.kvKey}>{field.key}</span>
      <div className={styles.kvValue}>
        {field.type === "string[]" ? (
          <ChipInput
            value={list}
            onChange={onChange}
            inputAriaLabel={field.key}
            placeholder="not given"
            validate={(chip) =>
              field.allowed && field.allowed.length > 0
                ? field.allowed.includes(chip)
                  ? null
                  : `${field.key} does not accept ${chip}`
                : null
            }
          />
        ) : field.type === "bool" ? (
          <Select
            aria-label={field.key}
            value={text}
            onChange={onChange}
            options={[
              { value: "", label: NOT_GIVEN, muted: true },
              { value: "true", label: "yes" },
              { value: "false", label: "no" },
            ]}
          />
        ) : field.allowed && field.allowed.length > 0 ? (
          <Select
            aria-label={field.key}
            value={text}
            onChange={onChange}
            options={[
              { value: "", label: NOT_GIVEN, muted: true },
              ...field.allowed.map((v) => ({ value: v, label: v })),
            ]}
          />
        ) : (
          <Input
            aria-label={field.key}
            value={text}
            placeholder="not given"
            inputMode={isNumberType(field) ? "numeric" : undefined}
            onChange={(e) => onChange(e.target.value)}
            error={err ?? undefined}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Why this age cannot be saved, or null — `numberError`'s job for the one box
 * that is not a card key, in the same register and for the same reason: the
 * server refuses all four of these, and a refusal the dialog can name is not
 * worth a round trip that comes back as a generic 400.
 *
 * An EMPTY box is not an error. It is the whole point of the field.
 */
function accountAgeError(raw: string): string | null {
  const text = raw.trim();
  if (text === "") return null;
  const n = Number(text);
  if (!Number.isFinite(n)) return "Not a number.";
  if (!Number.isInteger(n)) return "A whole number of days.";
  if (n < 0) return "An account cannot be registered in the future.";
  // The ceiling UNGROUPED, unlike every other number this dialog prints: it is
  // the identifier of one limit — the same 3650 the boxes above are capped at
  // and the same one `bots.account_age` is refused past — and «3 650» read as
  // a quantity somebody would have to count back to the constant.
  if (n > MAX_AGE_DAYS) return `Past the ${MAX_AGE_DAYS}-day ceiling.`;
  return null;
}

/* --- text in, JSON out ---------------------------------------------------- */

/** The row's stored card, as the boxes that show it. */
function draftOf(
  input: RosterInput,
  keys: CardKey[],
): Record<string, DraftValue> {
  const out: Record<string, DraftValue> = {};
  for (const k of keys) {
    const bag = k.kind === "stat" ? input.stats : input.meta;
    const raw = bag?.[k.key];
    if (raw === undefined || raw === null) {
      out[columnID(k)] = k.type === "string[]" ? [] : "";
      continue;
    }
    if (Array.isArray(raw)) {
      out[columnID(k)] = raw.map((v) => String(v));
      continue;
    }
    if (typeof raw === "object") {
      out[columnID(k)] = JSON.stringify(raw);
      continue;
    }
    out[columnID(k)] = String(raw);
  }
  return out;
}

/** …and back. A box left empty leaves its key out of the object entirely. */
function bagOf(
  keys: CardKey[],
  values: Record<string, DraftValue>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    const raw = values[columnID(k)];
    if (k.type === "string[]") {
      const list = Array.isArray(raw) ? raw : [];
      if (list.length > 0) out[k.key] = list;
      continue;
    }
    const text = typeof raw === "string" ? raw.trim() : "";
    if (text === "") continue;
    if (k.type === "bool") {
      out[k.key] = text === "true";
      continue;
    }
    if (isNumberType(k)) {
      out[k.key] = Number(text);
      continue;
    }
    out[k.key] = text;
  }
  return out;
}

